import { inject, Injectable } from '@angular/core';
import type { EventsChannel } from 'aws-amplify/data';
import { events } from 'aws-amplify/data';
import { from, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '@analytics/environments/environment';
import { reportAnalyticsError } from '../utils/analytics-error-reporting.util';
import { ViewerAuthService } from './viewer-auth.service';
import { Sha256 } from '@aws-crypto/sha256-js';
import {
	CognitoIdentityClient,
	Credentials,
	GetCredentialsForIdentityCommand,
	GetCredentialsForIdentityCommandOutput,
	GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { SignatureV4 } from '@aws-sdk/signature-v4';

interface CredentialsCache {
	result: GetCredentialsForIdentityCommandOutput;
	expiration: number;
}

@Injectable({
	providedIn: 'root',
})
export class RequestService {
	private readonly LEGACY_CREDENTIALS_KEY = 'aws_credentials';
	#inFlightCredentials: Promise<GetCredentialsForIdentityCommandOutput> | null =
		null;
	private readonly viewerAuth = inject(ViewerAuthService);

	/**
	 * Report currently being viewed. When a PRIVATE share has a wrapper JWT for it,
	 * GraphQL switches from guest IAM SigV4 to `Authorization: JeptoViewer`.
	 */
	private activeReportId: string | null = null;

	setActiveReport(reportId: string | null): void {
		this.activeReportId = reportId ? reportId.toLowerCase() : null;
	}

	private activeViewerToken(): string | null {
		return this.activeReportId
			? this.viewerAuth.getJwtForReport(this.activeReportId)
			: null;
	}

	private get credentialsCacheKey(): string {
		const { region, customEndpoint } = environment.appSyncConfig;
		const { identityPoolId } = environment.cognitoSettings;
		return `aws_credentials:${region}:${identityPoolId}:${customEndpoint}`;
	}

	private getCachedCredentials(): GetCredentialsForIdentityCommandOutput | null {
		const raw = localStorage.getItem(this.credentialsCacheKey);
		if (!raw) return null;
		try {
			const cache: CredentialsCache = JSON.parse(raw);
			if (
				cache.expiration > Date.now() + 300000 &&
				cache?.result?.IdentityId
			) {
				return cache.result;
			}
		} catch {
			localStorage.removeItem(this.credentialsCacheKey);
		}
		return null;
	}

	private cacheCredentials(
		result: GetCredentialsForIdentityCommandOutput
	): void {
		if (result.Credentials && result.Credentials.Expiration) {
			localStorage.setItem(
				this.credentialsCacheKey,
				JSON.stringify({
					result,
					expiration: result.Credentials.Expiration.getTime(),
				})
			);
		}
	}

	private clearCredentialsCache(): void {
		localStorage.removeItem(this.credentialsCacheKey);
		localStorage.removeItem(this.LEGACY_CREDENTIALS_KEY);
	}

	private isUnauthorizedGraphQlResponse(gqlResponse: any): boolean {
		const errors = gqlResponse?.errors;
		if (!Array.isArray(errors)) return false;
		return errors.some((error: any) => {
			const message = String(error?.message ?? '').toLowerCase();
			const errorType = String(
				error?.errorType ?? error?.extensions?.errorType ?? ''
			).toLowerCase();
			return (
				message.includes('unauthorized') ||
				errorType.includes('unauthorized')
			);
		});
	}

	getCachedIdentityId(): string | null {
		return this.getCachedCredentials()?.IdentityId ?? null;
	}

	async getIdentityId(): Promise<string> {
		const result = await this.getUnauthenticatedCredentials();
		return result.IdentityId;
	}

	private async callAppSyncWithBearer(
		query: string,
		variables: Record<string, any>,
		token: string
	): Promise<any> {
		const response = await fetch(environment.appSyncConfig.customEndpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: this.#viewerAuthorizationHeader(token),
			},
			body: JSON.stringify({ query, variables }),
		});

		const gqlResponse = await response.json();
		if (
			response.status === 401 ||
			this.isUnauthorizedGraphQlResponse(gqlResponse) ||
			this.isInvalidViewerTokenResponse(gqlResponse)
		) {
			const error = new Error('AppSyncViewerUnauthorized');
			(error as any).gqlResponse = gqlResponse;
			throw error;
		}
		return gqlResponse;
	}

	#viewerAuthorizationHeader(token: string): string {
		const trimmed = token.trim();
		const withoutScheme = trimmed.replace(/^(JeptoViewer|Bearer)\s+/i, '');
		return `JeptoViewer ${withoutScheme}`;
	}

	private isInvalidViewerTokenResponse(gqlResponse: any): boolean {
		const errors = gqlResponse?.errors;
		if (!Array.isArray(errors)) {
			return false;
		}
		return errors.some((error: any) => {
			const errorType = String(error?.errorType ?? '').toLowerCase();
			return errorType.includes('badrequestexception');
		});
	}

	private async callAppSyncWithIam(
		query: string,
		variables: Record<string, any>,
		forceRefreshCredentials = false
	): Promise<any> {
		const result = await this.getUnauthenticatedCredentials(
			forceRefreshCredentials
		);
		const credentials = result.Credentials;
		const hostname = environment.appSyncConfig.customEndpoint
			.replace('https://', '')
			.replace('/graphql', '');

		const signer = new SignatureV4({
			credentials: {
				accessKeyId: credentials.AccessKeyId,
				secretAccessKey: credentials.SecretKey,
				sessionToken: credentials.SessionToken,
			},
			region: environment.appSyncConfig.region,
			service: 'appsync',
			sha256: Sha256,
		});

		const request = new HttpRequest({
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				host: hostname,
			},
			hostname,
			body: JSON.stringify({ query, variables }),
			path: '/graphql',
			protocol: 'https:',
		});

		const signedRequest = await signer.sign(request);
		const response = await fetch(environment.appSyncConfig.customEndpoint, {
			method: signedRequest.method,
			headers: signedRequest.headers,
			body: signedRequest.body,
		});

		const gqlResponse = await response.json();
		if (
			response.status === 401 ||
			this.isUnauthorizedGraphQlResponse(gqlResponse)
		) {
			const error = new Error('AppSyncUnauthorized');
			(error as any).gqlResponse = gqlResponse;
			throw error;
		}
		return gqlResponse;
	}

	async execute(
		operation: string,
		variables: Record<string, any> = {}
	): Promise<any> {
		const viewerToken = this.activeViewerToken();
		if (viewerToken) {
			try {
				return await this.callAppSyncWithBearer(
					operation,
					variables,
					viewerToken
				);
			} catch (error) {
				if (
					error instanceof Error &&
					error.message === 'AppSyncViewerUnauthorized' &&
					this.activeReportId
				) {
					this.viewerAuth.clearJwtForReport(this.activeReportId);
				}
				throw error;
			}
		}
		return this.executeAsGuest(operation, variables);
	}

	/**
	 * Guest IAM SigV4 regardless of any viewer session — used by the share gate
	 * (`getViewAccess`, `createViewAccessRequest`), which must stay anonymous.
	 */
	async executeAsGuest(
		operation: string,
		variables: Record<string, any> = {}
	): Promise<any> {
		try {
			return await this.callAppSyncWithIam(operation, variables);
		} catch (error) {
			if (
				error instanceof Error &&
				error.message === 'AppSyncUnauthorized'
			) {
				this.clearCredentialsCache();
				return this.callAppSyncWithIam(operation, variables, true);
			}
			throw error;
		}
	}

	query({
		query,
		variables = {},
	}: {
		query: string;
		variables?: Record<string, any>;
	}): Observable<any> {
		return from(this.execute(query, variables)).pipe(
			catchError(error => throwError(() => error))
		);
	}

	async getEventsChannel(channel: string): Promise<EventsChannel> {
		try {
			return await this.connectToEventsChannel(channel);
		} catch (error) {
			if (error?.errors?.[0]?.message === 'Unauthorized') {
				// Retry once on unauthorized error
				return this.connectToEventsChannel(channel);
			}
			throw error;
		}
	}

	private async connectToEventsChannel(
		channel: string
	): Promise<EventsChannel> {
		try {
			const eventsChannel = await events.connect(channel, {
				authMode: 'iam',
			});
			return eventsChannel;
		} catch (error) {
			reportAnalyticsError(
				`Failed to connect to events channel: ${channel}`,
				error,
				{ feature: 'analytics', operation: 'eventsChannelConnect' }
			);
			throw error;
		}
	}

	async getUnauthenticatedCredentials(
		forceRefresh = false
	): Promise<GetCredentialsForIdentityCommandOutput> {
		if (!forceRefresh) {
			const cached = this.getCachedCredentials();
			if (cached) return cached;
		}

		// Share one Cognito round-trip between concurrent callers; cleared once
		// settled so a failed request can be retried.
		this.#inFlightCredentials ??=
			this.fetchUnauthenticatedCredentials().finally(() => {
				this.#inFlightCredentials = null;
			});
		return this.#inFlightCredentials;
	}

	private async fetchUnauthenticatedCredentials(): Promise<GetCredentialsForIdentityCommandOutput> {
		const client = new CognitoIdentityClient({
			region: environment.appSyncConfig.region,
		});

		const identity = await client.send(
			new GetIdCommand({
				IdentityPoolId: environment.cognitoSettings.identityPoolId,
			})
		);

		const result = await client.send(
			new GetCredentialsForIdentityCommand({
				IdentityId: identity.IdentityId,
			})
		);

		this.cacheCredentials(result);

		return result;
	}
}
